import React from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import { formatImageUrl } from '@benzinga/utils';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Ticker, isCryptoTickerSymbol } from '@benzinga/ticker-ui';
import { PostElapsed } from '@benzinga/news';
import { getSponsoredContentLabel, isPaywallArticle, isProOnlyPost } from '@benzinga/article-manager';
import { ProBadge, EdgeBadge } from '@benzinga/core-ui';
import { ArticleLinkWrapper } from './ArticleLinkWrapper';

const isPrimary = (stock: { primary?: boolean; Primary?: boolean }) => Boolean(stock?.primary ?? stock?.Primary);

const getVisibleTickers = (stocks: StoryObject['stocks'], maxSlots: number = 3): NonNullable<StoryObject['stocks']> => {
  if (!stocks) return [];
  const primaryFirst = [...stocks].sort((a, b) => (isPrimary(b) ? 1 : 0) - (isPrimary(a) ? 1 : 0));
  const result: NonNullable<StoryObject['stocks']> = [];
  let usedSlots = 0;

  for (const stock of primaryFirst) {
    const slotCost = isCryptoTickerSymbol(stock.name) ? 2 : 1;
    if (usedSlots + slotCost > maxSlots) break;
    result.push(stock);
    usedSlots += slotCost;
  }

  return result;
};

export type ArticleCardVariant = 'horizontal' | 'vertical-main' | 'vertical-compact' | 'split-main' | 'split-side';

export interface ArticleCardProps {
  article: StoryObject;
  variant: ArticleCardVariant;
  highPriority?: boolean;
  showTicker?: boolean;
  fullRoundedImage?: boolean;
  fillHeight?: boolean;
  className?: string;
  sponsored?: boolean;
}

export const articleLinkProps = (article: StoryObject) => ({
  article: { url: article.url, nodeId: article.nodeId },
});

export const ArticleCard = ({
  article,
  variant,
  highPriority,
  showTicker = true,
  fullRoundedImage,
  fillHeight,
  className,
  sponsored,
}: ArticleCardProps) => {
  if (!article) return null;

  const imageUrl = formatImageUrl(article, true, true, true);
  const stocksOrTickers =
    (article as { tickers?: StoryObject['stocks'] }).tickers ??
    (article as { Tickers?: StoryObject['stocks'] }).Tickers ??
    article.stocks;
  const tickers = getVisibleTickers(stocksOrTickers ?? null);
  const mainTicker = tickers[0] ?? article.stocks?.[0];
  const sponsoredData = getSponsoredContentLabel(article);
  const showSponsoredLabel = sponsored || sponsoredData.isSponsored;
  const sponsoredLabel = sponsoredData.label || 'Partner Content';
  const hasEdgeBadge = isPaywallArticle(article);
  const showProBadge = isProOnlyPost(article);
  const hasBadges = showProBadge || hasEdgeBadge;

  if (variant === 'horizontal') {
    return (
      <ArticleLinkWrapper {...articleLinkProps(article)} className="block h-full">
        <div className="flex gap-3 bg-white rounded-lg overflow-hidden h-full border border-bzblue-400 hover:shadow-sm transition-shadow">
          {imageUrl && (
            <div className="flex-shrink-0 w-24 h-full min-h-[100px] overflow-hidden relative">
              <Image
                alt=""
                fill
                sizes="96px"
                src={imageUrl}
                style={{ objectFit: 'cover' }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0 flex flex-col gap-1 py-3 pr-3">
            {showSponsoredLabel && <span className="text-xs font-semibold text-blue-500">{sponsoredLabel}</span>}
            <h4 className="text-base font-semibold leading-normal text-gray-900 hover:text-blue-600 transition-colors line-clamp-3 min-w-0 flex-1">
              {hasBadges && (
                <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
                  {showProBadge ? <ProBadge /> : EdgeBadge}
                </span>
              )}
              <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title || '') }} />
            </h4>
          </div>
        </div>
      </ArticleLinkWrapper>
    );
  }

  if (variant === 'vertical-main') {
    return (
      <div
        className={classNames(
          'flex flex-col @[480px]:flex-row',
          fillHeight ? 'flex-1' : '@[480px]:h-[190px]',
          '@[480px]:min-h-[190px]',
        )}
      >
        {imageUrl && (
          <ArticleLinkWrapper {...articleLinkProps(article)}>
            <div className="flex-shrink-0 w-full @[480px]:w-[15rem] @[768px]:w-[26rem] h-64 @[480px]:h-full bg-gray-200 overflow-hidden relative">
              <Image
                alt=""
                fill
                fetchPriority={highPriority ? 'high' : undefined}
                priority={highPriority}
                sizes="(max-width: 479px) 100vw, (max-width: 767px) 15rem, 26rem"
                src={imageUrl}
                style={{ objectFit: 'cover' }}
              />
            </div>
          </ArticleLinkWrapper>
        )}
        <div className={classNames('flex-1 min-w-0 flex flex-col p-4', showTicker ? 'gap-2' : 'gap-1 justify-center')}>
          {showTicker && tickers.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {tickers.map((ticker, idx) => (
                <Ticker key={`${ticker.name}-${idx}`} symbol={ticker.name} />
              ))}
            </div>
          )}
          <ArticleLinkWrapper {...articleLinkProps(article)}>
            <h3
              className={classNames(
                'text-lg @[480px]:text-xl font-bold line-clamp-3 leading-tight text-gray-900 hover:text-blue-600 min-w-0',
                showTicker && 'flex-1',
              )}
            >
              {hasBadges && (
                <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
                  {showProBadge ? <ProBadge /> : EdgeBadge}
                </span>
              )}
              <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title || '') }} />
            </h3>
            {article.teaser && (
              <p
                className="text-sm text-gray-600 line-clamp-2 mt-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.teaser || '') }}
              />
            )}
          </ArticleLinkWrapper>
          <div className={classNames('text-xs text-bzgray-600', showTicker ? 'mt-auto' : 'mt-1')}>
            {article.created && <PostElapsed created={article.created} />}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'vertical-compact') {
    return (
      <div className={showTicker ? 'h-full' : 'block'}>
        <div className={classNames('flex flex-col', showTicker && 'h-full')}>
          {imageUrl && (
            <ArticleLinkWrapper {...articleLinkProps(article)}>
              <div
                className={classNames(
                  'w-full h-24 bg-gray-200 overflow-hidden flex-shrink-0 relative',
                  fullRoundedImage ? 'rounded-md' : 'rounded-t-md',
                )}
              >
                <Image
                  alt=""
                  fill
                  fetchPriority={highPriority ? 'high' : undefined}
                  priority={highPriority}
                  sizes="(max-width: 767px) 50vw, 190px"
                  src={imageUrl}
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </ArticleLinkWrapper>
          )}
          <div className={classNames('flex flex-col p-2', showTicker ? 'flex-1 gap-2' : 'gap-1')}>
            {showTicker && mainTicker && (
              <div>
                <Ticker symbol={mainTicker.name} />
              </div>
            )}
            <ArticleLinkWrapper {...articleLinkProps(article)} className={showTicker ? 'flex-1' : undefined}>
              <h4 className="text-sm font-semibold line-clamp-3 leading-tight text-gray-900 hover:text-blue-600 min-w-0">
                {hasBadges && (
                  <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
                    {showProBadge ? <ProBadge /> : EdgeBadge}
                  </span>
                )}
                <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title || '') }} />
              </h4>
            </ArticleLinkWrapper>
            {article.created && (
              <span className={classNames('text-xs text-bzgray-600', showTicker && !fullRoundedImage && 'mt-auto')}>
                <PostElapsed created={article.created} />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'split-main') {
    return (
      <div className={classNames('flex flex-col h-full rounded-lg overflow-hidden bg-white', className)}>
        {imageUrl && (
          <ArticleLinkWrapper
            {...articleLinkProps(article)}
            className="w-full h-[240px] overflow-hidden flex-shrink-0 block relative"
          >
            <Image
              alt=""
              fill
              sizes="(max-width: 1023px) 100vw, 50vw"
              src={imageUrl}
              style={{ objectFit: 'cover' }}
            />
          </ArticleLinkWrapper>
        )}
        <div className={classNames('flex-1 flex flex-col p-5', showTicker ? 'gap-3' : 'gap-2')}>
          <ArticleLinkWrapper {...articleLinkProps(article)}>
            <h3
              className={classNames(
                'text-lg lg:text-xl font-bold leading-tight text-gray-900 hover:text-blue-600 transition-colors line-clamp-3 min-w-0',
                showTicker && 'flex-1',
              )}
            >
              {hasBadges && (
                <span className="inline-block align-middle mr-2 flex-shrink-0 scale-125">
                  {showProBadge ? <ProBadge /> : EdgeBadge}
                </span>
              )}
              <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title || '') }} />
            </h3>
          </ArticleLinkWrapper>
          <div className={classNames('flex flex-col gap-2', showTicker ? 'mt-auto pt-2' : 'mt-1')}>
            {showTicker && tickers.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {tickers.map((ticker, idx) => (
                  <Ticker key={`${ticker.name}-${idx}`} symbol={ticker.name} variant="box" hideChange={false} />
                ))}
              </div>
            )}
            <div className="text-xs text-bzgray-600">
              {article.created && <PostElapsed created={article.created} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'split-side') {
    return (
      <div
        className={classNames('flex bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow', className)}
      >
        {imageUrl && (
          <ArticleLinkWrapper
            {...articleLinkProps(article)}
            className="flex-shrink-0 w-[100px] h-[100px] block relative"
          >
            <Image
              alt=""
              fill
              sizes="100px"
              src={imageUrl}
              style={{ objectFit: 'cover' }}
            />
          </ArticleLinkWrapper>
        )}
        <div
          className={classNames(
            'flex-1 min-w-0 flex flex-col p-2 pr-3 min-h-0 overflow-hidden',
            showTicker ? 'justify-between' : 'gap-1',
          )}
        >
          <ArticleLinkWrapper {...articleLinkProps(article)}>
            <h4 className="title-text text-base font-semibold line-clamp-3 md:line-clamp-2 text-gray-900 hover:text-blue-600">
              {hasBadges && (
                <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
                  {showProBadge ? <ProBadge /> : EdgeBadge}
                </span>
              )}
              <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title || '') }} />
            </h4>
          </ArticleLinkWrapper>
          <div
            className={classNames('flex items-center gap-2 mt-1 min-w-0 flex-shrink-0', {
              'justify-between': showTicker,
              'justify-end': !showTicker,
            })}
          >
            {showTicker && (
              <div className="flex items-center gap-2 min-w-0 overflow-hidden flex-nowrap">
                {tickers.slice(0, 2).map(stock => (
                  <Ticker
                    key={`article-card-ticker-${stock.name}`}
                    symbol={stock.name}
                    variant="box"
                    hideChange={false}
                  />
                ))}
              </div>
            )}
            <div className="text-xs text-bzgray-600 whitespace-nowrap flex-shrink-0">
              {article.created && <PostElapsed created={article.created} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
